import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Image from 'next/image';
import { DateTime } from 'luxon';
import { Swiper, SwiperSlide } from 'swiper/react';
import cn from 'classnames';
import { Translate, useTranslate } from '~/i18n';
import Modal from '~/components/common/modal';
import { Section } from '~/components/common/section';
import { Button } from '~/components/common/button';
import { Input } from '~/components/common/forms';
import RatingStars from '~/components/common/rating-stars';
import { successMessage, errorMessage } from '~/utils/toasts';
import { ReviewProps } from '~/interfaces/review';
import { ChatReplyIcon, UserIcon, ArrowLeft, ArrowRight } from '~/svgs';
import { addReviewReply } from '~/apis/reviews/add-review-reply.api';
import { editReviewReply } from '~/apis/reviews/edit-review-reply.api';

type ReplyModalProps = {
   review: ReviewProps;
   refetchReviews: () => void;
   closeModal: () => void;
   open: boolean;
};

const ReviewReplyModal = ({ review, refetchReviews, closeModal, open }: ReplyModalProps) => {
   const { lang, translate, dir } = useTranslate();
   const { register, handleSubmit, reset, watch } = useForm({
      defaultValues: {
         content: review?.reply?.content || '',
      },
   });
   const [loading, setLoading] = useState(false);

   const submitReply = async (values: any) => {
      if (loading || !values.content) return;
      setLoading(true);
      const { error } = review?.reply?.id
         ? await editReviewReply(review?.reply?.id, values.content)
         : await addReviewReply(review?.id, values.content);
      if (error) {
         errorMessage(translate({ id: 'common:some error' }));
         setLoading(false);
      } else {
         closeModal();
         reset();
         refetchReviews();
         successMessage(translate({ id: 'reviews:reply added' }));
         setLoading(false);
      }
   };

   const cancelBtnHandler = () => {
      reset();
      closeModal();
   };

   return (
      <>
         <Modal size="lg" open={open} onClickOutside={closeModal}>
            <Modal.Body>
               <Section className="py-10">
                  <div className="w-full flex gap-4 px-10">
                     <div>
                        {review?.user?.thumbnail ? (
                           <Image
                              src={review?.user?.thumbnail}
                              width={60}
                              height={60}
                              className="rounded-full"
                              layout="fixed"
                           />
                        ) : (
                           <div className="flex bg-grey-500 p-5 rounded-full w-[60px] h-[60px] items-center justify-center">
                              <UserIcon width={30} height={30} />
                           </div>
                        )}
                     </div>
                     <div className="flex flex-col">
                        <span className="text-lg font-semibold mb-2">{review?.user?.name}</span>
                        <div className="flex gap-2">
                           <div className="flex flex-wrap text-sm gap-1">
                              <span className="text-grey-200">
                                 <Translate id="bookings:product" />:
                              </span>
                              <span className="font-semibold">{review?.product_name}</span>
                           </div>
                           <div className="flex flex-wrap text-sm gap-1">
                              <span className="text-grey-200">
                                 <Translate id="reviews:booking id" />:
                              </span>
                              <span className="font-semibold">{review?.booking_id}</span>
                           </div>
                        </div>
                     </div>
                  </div>

                  <hr />

                  <div className="w-full py-5 px-10">
                     <div className="flex items-center gap-5">
                        <RatingStars rating_value={review?.overall_rating} color="white" />
                        <span className="text-grey-200">
                           {DateTime.fromJSDate(new Date(review?.created_at.replace(' ', 'T')))
                              .setLocale(lang)
                              .toFormat('dd - MM - yyyy')}
                        </span>
                        {review?.overall_rating < 2 ? (
                           <div className="bg-red text-sm text-white py-1.5 px-4 rounded-full">
                              <Translate id="reviews:requires attention" />
                           </div>
                        ) : null}
                     </div>
                     {review?.liked_comment ? (
                        <div>
                           <span className="text-sm font-semibold">
                              <Translate id="reviews:what i liked" />:
                           </span>
                           <p>{review?.liked_comment}</p>
                        </div>
                     ) : null}
                     {review?.not_liked_comment ? (
                        <div>
                           <span className="text-sm font-semibold">
                              <Translate id="reviews:what i didn't liked" />:
                           </span>
                           <p>{review?.not_liked_comment}</p>
                        </div>
                     ) : null}
                     {review?.photos?.length ? (
                        <div className="relative mt-3 !z-0 overflow-hidden max-w-[100vw] lg:w-full">
                           <Swiper
                              key={`${dir}_reply_${review?.id}`}
                              slidesPerView={1}
                              spaceBetween={1}
                              dir={dir}
                              navigation={{
                                 nextEl: `.replynext-${review?.id}`,
                                 prevEl: `.replyprev-${review?.id}`,
                              }}>
                              {review?.photos?.map((image: any, idx: any) => (
                                 <SwiperSlide key={idx} className="xs:p-0">
                                    <div className="relative h-52">
                                       <div
                                          className="relative h-full overflow-hidden transition-transform duration-500 ease-in-out delay-200"
                                          style={{ position: 'relative' }}>
                                          <Image
                                             layout="fill"
                                             className="object-cover rounded-lg transition-transform duration-500 delay-200 cursor-pointer"
                                             src={image}
                                             alt="image"
                                             quality={60}
                                          />
                                       </div>
                                    </div>
                                 </SwiperSlide>
                              ))}
                           </Swiper>
                           <div className="flex h-full z-10 absolute top-1/2 w-full px-3 justify-between">
                              <button
                                 className={cn(
                                    'swiper-arrow flex justify-center items-center bg-opacity-50 bg-white rounded-full h-8 w-8 focus:outline-none focus:ring-0',
                                    `replyprev-${review?.id}`
                                 )}
                                 aria-label="previous">
                                 {dir === 'ltr' ? <ArrowLeft /> : <ArrowRight />}
                              </button>
                              <button
                                 className={cn(
                                    'swiper-arrow flex justify-center items-center bg-opacity-50 bg-white rounded-full h-8 w-8 focus:outline-none focus:ring-0',
                                    `replynext-${review?.id}`
                                 )}
                                 aria-label="next">
                                 {dir === 'ltr' ? <ArrowRight /> : <ArrowLeft />}
                              </button>
                           </div>
                        </div>
                     ) : null}
                  </div>

                  <form onSubmit={handleSubmit(submitReply)}>
                     <div className="mb-10  mt-5 mx-10">
                        <Input
                           dir={lang === 'ar' ? 'rtl' : 'ltr'}
                           placeholder={translate({ id: 'reviews:write your public reply here' })}
                           id="description_en"
                           groupClassName="mb-0"
                           className="focus:z-10 resize-none"
                           type="textarea"
                           rows={5}
                           {...register('content')}
                        />
                        <div className="flex gap-4 justify-end mt-5">
                           <Button
                              onClick={() => cancelBtnHandler?.()}
                              disabled={loading}
                              theme="outline-primary"
                              className="w-32 h-12">
                              <Translate id="common:cancel" />
                           </Button>
                           <Button
                              disabled={loading || !watch('content')}
                              loading={loading}
                              type="submit"
                              theme="primary"
                              className="w-32 h-12 flex items-center gap-3">
                              <ChatReplyIcon width={20} height={20} />
                              <span>
                                 <Translate id="reviews:reply publicly" />
                              </span>
                           </Button>
                        </div>
                     </div>
                  </form>
               </Section>
            </Modal.Body>
         </Modal>
      </>
   );
};

export default ReviewReplyModal;
